import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    first,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { PickingTaskOrchestrator } from '../services/picking-task-orchestrator.service';
import { HandlingUnitAssignmentService } from '../services/handling-unit-assignment.service';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { RoutesForPicking } from './picking.routes';
import { PickingTask } from '../models/picking-task.model';
import {
    ContainerDto,
    ContainerFacade,
    EquipmentDto,
    EquipmentTypeDto,
    EquipmentTypeFacade,
    PickFacade,
    PickListDataService,
    ProjectFacade,
    ProjectLineItemFacade,
    SlotDto,
    SlotFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import { asArraySafe } from '@pf/shared-utility';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    PickingContext,
    TargetContainers,
} from '../models/picking-context.model';
import { ProjectPick } from '../models/project-pick.model';
import {
    ContainerStatus,
    SlotType,
    WarehouseEquipmentCategory,
    ZoneType,
} from '@control-tower/platform-wms';
import { isEqual, orderBy } from 'lodash-es';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { LoggerService, UserPermissionCheckService } from '@pf/shared-services';
import {
    EQUIPMENT_GROUPS,
    EquipmentGroup,
    isHandlingLtl,
} from '../models/cpw-equipment-types';

@UntilDestroy()
@Injectable()
export class PickingFacade {
    private readonly slotFacade = inject(SlotFacade);
    private readonly containerFacade = inject(ContainerFacade);
    private readonly projectFacade = inject(ProjectFacade);
    private readonly projectLineItemFacade = inject(ProjectLineItemFacade);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly pickFacade = inject(PickFacade);
    private taskStateService = inject(CpwTaskStateService);
    private routes = inject(RoutesForPicking);
    private pickListDataService = inject(PickListDataService);
    private readonly taskOrchestrator = inject(PickingTaskOrchestrator);
    private readonly equipmentService = inject(HandlingUnitAssignmentService);
    private readonly equipmentTypeFacade = inject(EquipmentTypeFacade);
    private userPositionFacade = inject(UserPositionFacade);
    private userPermissionCheckService = inject(UserPermissionCheckService);
    private logger = inject(LoggerService);
    private tasksLoadingState = new BehaviorSubject<
        'not-loaded' | 'loading' | 'loaded'
    >('not-loaded');
    private _activeProjectPick = new BehaviorSubject<ProjectPick | null>(null);

    activeProjectPick$ = this._activeProjectPick.asObservable();
    get activeProjectPick(): ProjectPick | null {
        return this._activeProjectPick.getValue();
    }

    private filteredTasks$ = combineLatest([
        this.taskStateService.tasks$,
        this.taskStateService.taskFilters$,
        this.equipmentTypeFacade
            .searchAll$()
            .pipe(startWith<EquipmentTypeDto[]>([])),
    ] as const).pipe(
        map(([tasks, filters, equipmentTypes]) => {
            const allowedGroups = this.computeAllowedGroups(
                filters.equipmentTypes,
                equipmentTypes
            );
            return (tasks as PickingTask[]).filter(task => {
                const zoneFilter =
                    filters.zones.length === 0 ||
                    task.projectSummaries
                        .map(summary => summary.zones)
                        .flat()
                        .some(zone => filters.zones.includes(zone.id));
                const equipmentTypesFilter =
                    !allowedGroups || allowedGroups.has(task.group);
                return zoneFilter && equipmentTypesFilter;
            });
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    private computeAllowedGroups(
        selectedEquipmentTypeIds: string[],
        allEquipmentTypes: EquipmentTypeDto[]
    ): Set<EquipmentGroup> | null {
        if (!selectedEquipmentTypeIds.length || !allEquipmentTypes.length) {
            return null;
        }
        const selectedCategories = new Set<WarehouseEquipmentCategory>();
        for (const id of selectedEquipmentTypeIds) {
            const match = allEquipmentTypes.find(eq => eq.id === id);
            if (match?.category) {
                selectedCategories.add(match.category);
            }
        }
        if (!selectedCategories.size) {
            return null;
        }
        const allowed = new Set<EquipmentGroup>();
        (Object.keys(EQUIPMENT_GROUPS) as EquipmentGroup[]).forEach(group => {
            if (
                EQUIPMENT_GROUPS[group].some(cat => selectedCategories.has(cat))
            ) {
                allowed.add(group);
            }
        });
        return allowed;
    }

    priorityPicks$ = this.filteredTasks$.pipe(
        map(tasks => {
            return tasks.filter(task => task.section === 'priority');
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    todaysPicks$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'today')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    futurePicks$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'future')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    pickingTasks$ = this.taskStateService.tasks$.pipe(
        map(tasks =>
            (tasks as PickingTask[]).filter(task => task.section === 'picking')
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activePickingTask$: Observable<PickingTask> =
        this.taskStateService.activeTask$.pipe(
            map(task => task as PickingTask),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    pickingContext$: Observable<PickingContext> = this.activePickingTask$.pipe(
        filter(task => !!task?.context),
        map(task => task.context),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activeTargetContainers$: Observable<TargetContainers | null> =
        this.pickingContext$.pipe(
            map(
                context =>
                    context.targetContainersByProject[
                        context.activeProjectId as string
                    ] ?? null
            ),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    assignedHandlingUnits$: Observable<EquipmentDto[]> =
        this.equipmentService.assignedHandlingUnits$;

    latestTargetContainer$: Observable<ContainerDto | null> = combineLatest([
        this.activeTargetContainers$,
        this.activeProjectPick$,
    ]).pipe(
        filter(
            ([targetContainers, projectPick]) =>
                !!targetContainers && !!projectPick
        ),
        map(
            ([targetContainers, _projectPick]: [
                TargetContainers | null,
                ProjectPick | null,
            ]) => {
                return targetContainers?.latestContainer || null;
            }
        ),
        distinctUntilChanged(isEqual),
        switchMap(latestContainer =>
            latestContainer
                ? this.containerFacade.getById$(latestContainer.containerId)
                : of(null)
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    recommendedTargetContainer$: Observable<ContainerDto | null> =
        combineLatest([this.pickingContext$, this.activeProjectPick$]).pipe(
            switchMap(([pickingContext, activeProjectPick]) => {
                const currentContainer =
                    activeProjectPick?.inventoryBundle?.container;
                if (
                    currentContainer?.id &&
                    !currentContainer.slot &&
                    currentContainer.equipment
                ) {
                    return this.containerFacade.getById$(currentContainer.id);
                }

                const activeForProject =
                    pickingContext.targetContainersByProject[
                        pickingContext.activeProjectId as string
                    ];

                if (activeForProject?.latestContainer) {
                    return this.latestTargetContainer$;
                }

                return this.assignedHandlingUnits$.pipe(
                    map(assignedHandlingUnits => {
                        const containersUsedByOtherProjects = Object.values(
                            pickingContext.targetContainersByProject
                        )
                            .map(targetContainers =>
                                asArraySafe(targetContainers.containers)
                            )
                            .flat();
                        const availableContainers = orderBy(
                            assignedHandlingUnits
                                .map(container =>
                                    asArraySafe(container.containers)
                                )
                                .flat(),
                            ['lpn'],
                            ['asc']
                        ).filter(
                            container =>
                                !containersUsedByOtherProjects.some(
                                    targetContainer =>
                                        container.id ===
                                        targetContainer.containerId
                                ) && !container.inventoryBundles?.length
                        );

                        const availableByType = availableContainers.filter(
                            c =>
                                !activeProjectPick?.equipmentType?.id ||
                                c.equipment?.equipmentType?.id ===
                                    activeProjectPick.equipmentType.id
                        );
                        return (
                            availableByType[0] || availableContainers[0] || null
                        );
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    ltlStagingSlot$: Observable<SlotDto | null> = this.activePickingTask$.pipe(
        filter(activeTask => !!activeTask?.isLtl),
        distinctUntilChanged((a, b) => a.id === b.id),
        switchMap(() =>
            this.slotFacade.getFirstEmptySlotByTypeAndZoneType$(
                SlotType.Staging,
                ZoneType.Packing
            )
        )
    );

    hasEquipmentForPick$: Observable<boolean> = combineLatest([
        this.assignedHandlingUnits$,
        this.activeProjectPick$,
    ]).pipe(
        map(([assignedHandlingUnits, activeProjectPick]) => {
            if (assignedHandlingUnits.length === 0) {
                return false;
            }

            if (!activeProjectPick?.equipmentType?.id) {
                return true;
            }

            return assignedHandlingUnits.some(
                equipment =>
                    equipment.equipmentType.id ===
                    activeProjectPick.equipmentType.id
            );
        })
    );

    canClaimMorePicks$ = combineLatest([
        this.assignedHandlingUnits$,
        this.todaysPicks$,
    ]).pipe(
        map(([assignedHandlingUnits, todaysPicks]) => {
            if (!todaysPicks?.length) {
                return false;
            }

            const nextPick = this.findNextPickByPriority(
                todaysPicks,
                assignedHandlingUnits
            );

            if (!nextPick) {
                return false;
            }

            // For Parts picks, verify available containers exist
            if (!nextPick.isLtl && !nextPick.isUniforms) {
                return (
                    assignedHandlingUnits
                        .map(equipment => asArraySafe(equipment.containers))
                        .flat()
                        .filter(
                            container =>
                                container.status === ContainerStatus.Unassigned
                        ).length > 0
                );
            }

            return true;
        })
    );

    getAssignedHandlingUnits() {
        return this.equipmentService.getAssignedHandlingUnits();
    }

    getUserSessionProjects$() {
        return this.userPositionFacade.active$.pipe(
            filter(Boolean),
            map(userPosition =>
                asArraySafe(
                    (userPosition!.context as PickingContext).projectIds
                )
            ),
            distinctUntilChanged(
                (a, b) => JSON.stringify(a) === JSON.stringify(b)
            ),
            switchMap(projectIds => {
                if (!projectIds.length) {
                    return of([]);
                }

                return this.projectFacade.getMany$(projectIds);
            })
        );
    }

    loadPickingProjects() {
        this.tasksLoadingState.next('loading');
        this.taskStateService.clearTasks();
        this.projectLineItemFacade.refreshProjectsOnLineItemUpdates();

        this.projectFacade.clearPages();

        return combineLatest({
            pendingProjects: this.projectFacade.pendingProjects$(),
            userSessionProjects: this.getUserSessionProjects$(),
            showLtlProjects: this.userPermissionCheckService.checkPermission$(
                'WMS:ForkliftOperator'
            ),
        }).pipe(
            untilDestroyed(this),
            debounceTime(300),
            tap(({ pendingProjects, userSessionProjects, showLtlProjects }) => {
                this.taskOrchestrator.orchestrate(
                    this.warehouseFacade.active!,
                    pendingProjects.filter(p => !p.isDeleted),
                    userSessionProjects.filter(p => !p.isDeleted),
                    showLtlProjects
                );
            }),
            tap(() => {
                this.tasksLoadingState.next('loaded');
            }),
            map(() => true),
            catchError(err => {
                this.logger.error(err);
                return of(false);
            })
        );
    }

    loadActivePickingTask$() {
        if (this.taskStateService.getActiveTask()) {
            return of(true);
        }

        return this.tasksLoadingState.pipe(
            filter(state => state !== 'loading'),
            take(1),
            switchMap(state => {
                if (state === 'not-loaded') {
                    return this.loadPickingProjects().pipe(delay(100));
                }

                return of(true);
            }),
            switchMap(() => this.pickingTasks$),
            tap(tasks => {
                if (tasks.length === 0) {
                    this.clearActivePickingTask();
                } else {
                    this.taskStateService.setActiveTask(tasks[0].id);
                }
            }),
            map(() => true)
        );
    }

    navigateToQueue() {
        this.routes.queue().navigate();
    }

    setActivePickingTask(taskId?: string): void {
        if (!taskId) {
            this.clearActivePickingTask();
            return;
        }

        this.taskStateService.setActiveTask(taskId);
        this.routes.pick().navigate();
    }

    claimActivePickingTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PickingTask) => {
                const pickListIds = activeTask.projectSummaries
                    .map(summary => summary.pickListIds)
                    .flat();
                return this.pickListDataService
                    .claimPicklists$(activeTask.warehouseId, pickListIds)
                    .pipe(
                        tap(() => {
                            this.taskStateService.claimActiveTask();
                        }),
                        tap(() => {
                            this.taskStateService
                                .updateTaskContext$<PickingContext>(
                                    activeTask.id,
                                    {
                                        projectIds:
                                            activeTask.projectSummaries.map(
                                                summary => summary.projectId
                                            ),
                                        pickListIds,
                                        warehouseId: activeTask.warehouseId,
                                        activePickLocationId: '',
                                        activeProjectLineItemId: '',
                                        activeProjectId: '',
                                        targetContainersByProject: {},
                                        latestHandlingUnitId: '',
                                    }
                                )
                                .subscribe();
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    claimNextPickingTask$() {
        return combineLatest([
            this.assignedHandlingUnits$,
            this.todaysPicks$,
        ]).pipe(
            first(),
            map(([assignedHandlingUnits, todaysPicks]) => {
                if (!todaysPicks?.length) {
                    return null;
                }
                return (
                    this.findNextPickByPriority(
                        todaysPicks,
                        assignedHandlingUnits
                    ) ?? null
                );
            }),
            switchMap((pickingTask: PickingTask | null) => {
                if (pickingTask) {
                    this.setActivePickingTask(pickingTask.id);
                    return this.claimActivePickingTask$();
                }
                return of(false);
            })
        );
    }

    completeTask$() {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PickingTask) => {
                return this.resetContext$(activeTask.id).pipe(
                    tap(() => {
                        this.taskStateService.clearActiveTask();
                        this.navigateToQueue();
                    }),
                    map(() => true),
                    catchError(() => of(true))
                );
            },
            of(false)
        );
    }

    releaseActivePickingTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PickingTask) => {
                const pickListIds = activeTask.projectSummaries
                    .map(summary => summary.pickListIds)
                    .flat();
                return this.pickListDataService
                    .unClaimPicklists$(activeTask.warehouseId, pickListIds)
                    .pipe(
                        switchMap(() => this.resetContext$(activeTask.id)),
                        tap(() => {
                            this.clearActivePickingTask();
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    clearActivePickingTask() {
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    setActivePickLocationId(projectPick: ProjectPick) {
        this.taskStateService
            .performWithActiveTask$((task: PickingTask) => {
                if (
                    task.context.activePickLocationId !==
                        projectPick.pickLocationId ||
                    task.context.activeProjectId !== projectPick.projectId
                ) {
                    this.taskStateService
                        .updateTaskContext$(projectPick.taskId, {
                            activePickLocationId: projectPick.pickLocationId,
                            activeProjectLineItemId:
                                projectPick.projectLineItemId,
                            activeProjectId: projectPick.projectId,
                        })
                        .subscribe();
                }
                this._activeProjectPick.next(projectPick);
                return of(true);
            }, of(false))
            .subscribe();
    }

    setLatestTargetContainer$(container: ContainerDto): Observable<string> {
        const containerType = container.containerType?.name || 'container';
        return this.taskStateService.performWithActiveTask$(
            (task: PickingTask) => {
                if (!task.context.activeProjectId) {
                    this.logger.error(
                        'No active project id found when setting latest target container'
                    );
                    return of('Invalid state. Refresh and try again.');
                }

                if (
                    !container.equipment?.id ||
                    !task.context.assignedHandlingUnitIds.includes(
                        container.equipment.id
                    )
                ) {
                    return of(
                        `Assigned handling unit(s) do not contain this ${containerType}.`
                    );
                }

                const targetContainersByProject =
                    task.context.targetContainersByProject;

                const conflictingProject = Object.entries(
                    targetContainersByProject
                ).find(
                    ([projectId, targetContainers]) =>
                        projectId !== task.context.activeProjectId &&
                        asArraySafe(targetContainers.containers).some(
                            tc => tc.containerId === container.id
                        )
                );

                if (conflictingProject) {
                    return of(
                        `This ${containerType} is already assigned to a different order. Please use the recommended ${containerType} or scan a new one.`
                    );
                }

                if (!targetContainersByProject[task.context.activeProjectId]) {
                    targetContainersByProject[task.context.activeProjectId] = {
                        latestContainer: null,
                        containers: [],
                    };
                }

                const targetContainer = {
                    containerId: container.id,
                    equipmentTypeId: container.equipment?.equipmentType?.id,
                };
                const projectTargetContainers =
                    targetContainersByProject[task.context.activeProjectId];

                if (!projectTargetContainers.containers) {
                    projectTargetContainers.containers = [];
                }

                projectTargetContainers.latestContainer = targetContainer;
                if (
                    !projectTargetContainers.containers.some(
                        targetContainer =>
                            targetContainer.containerId === container.id
                    )
                ) {
                    projectTargetContainers.containers.unshift(targetContainer);
                }

                this.taskStateService
                    .updateTaskContext$(task.id, {
                        latestHandlingUnitId: container.equipment?.id,
                        targetContainersByProject: targetContainersByProject,
                    } as Partial<PickingContext>)
                    .subscribe();

                return of('');
            },
            of(`Failed to assign ${containerType}.`)
        );
    }

    initializeLtlPalletContainer$(pick: ProjectPick): Observable<string> {
        return this.taskStateService.performWithActiveTask$(
            (task: PickingTask) => {
                const existingTarget =
                    task.context.targetContainersByProject[pick.projectId];
                if (existingTarget?.latestContainer?.containerId) {
                    return of(existingTarget.latestContainer.containerId);
                }

                return this.assignedHandlingUnits$.pipe(
                    first(),
                    switchMap(assignedHandlingUnits => {
                        const forklift = assignedHandlingUnits.find(hu =>
                            isHandlingLtl(hu.equipmentType)
                        );
                        if (!forklift) {
                            this.logger.error(
                                'No LTL equipment assigned for LTL pick.'
                            );
                            return of('');
                        }

                        if (!pick.inventoryBundle.container) {
                            this.logger.error(
                                'No container found for LTL pick.'
                            );
                            return of('');
                        }
                        const sourceContainerId = pick.inventoryBundle.container
                            .id as string;

                        const bundleQty =
                            pick.inventoryBundle.totalQuantity ??
                            pick.quantityNeeded;
                        const exactMatch = bundleQty === pick.quantityNeeded;

                        const palletContainer$: Observable<string> = exactMatch
                            ? this.containerFacade
                                  .setEquipment$(sourceContainerId, forklift.id)
                                  .pipe(map(() => sourceContainerId))
                            : this.containerFacade
                                  .createContainerByTypeCode$(
                                      'PL',
                                      new ContainerDto({
                                          status: ContainerStatus.Unassigned,
                                          warehouse: { id: pick.warehouseId },
                                          equipment: { id: forklift.id },
                                      })
                                  )
                                  .pipe(map(created => created.id));

                        return palletContainer$.pipe(
                            switchMap(palletContainerId => {
                                const targetContainersByProject =
                                    task.context.targetContainersByProject;
                                const targetContainer = {
                                    containerId: palletContainerId,
                                    equipmentTypeId: forklift.equipmentType?.id,
                                };
                                targetContainersByProject[pick.projectId] = {
                                    latestContainer: targetContainer,
                                    containers: [targetContainer],
                                };
                                return this.taskStateService
                                    .updateTaskContext$(task.id, {
                                        targetContainersByProject,
                                    } as Partial<PickingContext>)
                                    .pipe(map(() => palletContainerId));
                            })
                        );
                    })
                );
            },
            of('')
        );
    }

    completePick$(pick: ProjectPick, containerId: string, slotId?: string) {
        return this.pickFacade
            .completePick$(
                pick.inventoryBundle.id,
                containerId,
                pick.quantityNeeded,
                pick.pickLocationId
            )
            .pipe(
                switchMap(pickResult => {
                    if (!slotId) {
                        return of(pickResult);
                    }
                    return this.containerFacade
                        .assignSlot$(containerId, slotId)
                        .pipe(map(() => pickResult));
                }),
                switchMap(pickResult =>
                    this.equipmentService
                        .refreshAssignedBundles$()
                        .pipe(map(() => pickResult))
                ),
                map(() => true)
            );
    }

    splitPick$(pick: ProjectPick, newQuantity: number) {
        return this.pickFacade
            .splitPick$(
                pick.warehouseId,
                pick.pickId,
                pick.pickLocationId,
                newQuantity
            )
            .pipe(
                switchMap(() => {
                    if (newQuantity === 0) {
                        pick.quantityNeeded = 0;
                        return this.completePick$(
                            pick,
                            pick.inventoryBundle.container!.id!
                        );
                    }
                    return this.equipmentService
                        .refreshAssignedBundles$()
                        .pipe(map(() => true));
                }),
                catchError(() => of(false))
            );
    }

    cancelPick$(pickId: string, pickLocationId: string) {
        return this.pickFacade.cancelPick$(pickId, [pickLocationId]).pipe(
            switchMap(() =>
                this.equipmentService
                    .refreshAssignedBundles$()
                    .pipe(map(() => true))
            ),
            catchError(() => of(false))
        );
    }

    private resetContext$(activeTaskId: string) {
        return this.taskStateService.updateTaskContext$(activeTaskId, {
            projectIds: [],
            latestHandlingUnitId: '',
            activeProjectLineItemId: '',
            activeProjectId: '',
            targetContainersByProject: {},
        });
    }

    private isHandlingLtl(assignedHandlingUnits: EquipmentDto[]) {
        if (!assignedHandlingUnits?.length) {
            return false;
        }
        return assignedHandlingUnits.some(hu =>
            isHandlingLtl(hu.equipmentType)
        );
    }

    private findNextPickByPriority(
        todaysPicks: PickingTask[],
        assignedHandlingUnits: EquipmentDto[]
    ): PickingTask | undefined {
        const isLtlOperator = this.isHandlingLtl(assignedHandlingUnits);

        if (isLtlOperator) {
            // LTL operators: prefer LTL, then Parts, then Uniforms
            return (
                todaysPicks.find(pick => pick.isLtl) ??
                todaysPicks.find(pick => !pick.isLtl && !pick.isUniforms) ??
                todaysPicks.find(pick => pick.isUniforms)
            );
        }

        // Parts operators: prefer Parts, then Uniforms, then LTL
        return (
            todaysPicks.find(pick => !pick.isLtl && !pick.isUniforms) ??
            todaysPicks.find(pick => pick.isUniforms) ??
            todaysPicks.find(pick => pick.isLtl)
        );
    }
}
